import {
  trackSelfDescribingEvent,
  trackStructEvent,
} from '@snowplow/browser-tracker';
import { useCallback, useMemo } from 'react';

import { Sort } from 'api/endpoints/intel/intel.enums';

import { csvToArray, debugSnowplow } from '../analytics.utils';
import { TRACKER_IDS, StructuredAnalyticsCategory } from '../constants';
import { createSnowplowEvent } from '../snowplow/utils';

import type { LogEventType, StateType } from '../AnalyticsProvider.types';
import type { SnowplowUnstructuredEvent } from '../snowplow/types';
import type { FiltersFromURLType } from 'hooks/alerts/use-filters-from-url';
import type { ValueOf } from 'type-fest';

const useLogEvent = ({
  analytics,
  debug = false,
  filtersFromURL,
}: {
  analytics: StateType;
  debug?: boolean;
  filtersFromURL: FiltersFromURLType;
}) => {
  const filterData = useMemo(() => {
    const {
      boardGroups,
      contentTypes,
      dateRange,
      priorities,
      rivalIds,
      sort,
      sources,
      states,
      topics,
    } = filtersFromURL;

    return {
      sort: sort || Sort.NEWEST,
      dateRange,
      boardGroups: csvToArray(boardGroups),
      rivalIds: csvToArray(rivalIds),
      states: csvToArray(states),
      priorities: csvToArray(priorities),
      sources: csvToArray(sources),
      contentTypes: csvToArray(contentTypes),
      topics: csvToArray(topics),
    };
  }, [filtersFromURL]);

  return useCallback(
    ({ event, data }: LogEventType) => {
      if (!analytics.providers?.sp?.isInitialized) {
        return;
      }

      /* Structured events (legacy) */
      if (
        Object.values(StructuredAnalyticsCategory).includes(
          event.category as ValueOf<typeof StructuredAnalyticsCategory>,
        )
      ) {
        trackStructEvent(event, [TRACKER_IDS.sp.legacy]);
        return;
      }

      /* Unstructured events (recommended) */
      const snowplowEventPayload = createSnowplowEvent(
        event as SnowplowUnstructuredEvent,
        {
          eventData: data,
          filterData,
        },
      );

      trackSelfDescribingEvent(snowplowEventPayload, [TRACKER_IDS.sp.default]);

      if (debug) {
        debugSnowplow('called trackSelfDescribingEvent with\n%o', {
          data: snowplowEventPayload,
        });
      }
    },
    [analytics, debug, filterData],
  );
};

export default useLogEvent;
